import type { ProformGlassPattern } from './proform-glass-presets';

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));
const smooth = (n: number) => {
  const t = clamp01(n);
  return t * t * (3 - 2 * t);
};
const tau = Math.PI * 2;
const mod = (n: number, period: number) => ((n % period) + period) % period;
const fract = (n: number) => n - Math.floor(n);

function hash(x: number, y: number, seed: number) {
  return fract(Math.sin(x * 127.1 + y * 311.7 + seed * 17.31) * 43758.5453);
}

/** Periodic value noise: identical borders on every tile, stable across selections/renders. */
function noise(u: number, v: number, cells: number, seed: number) {
  const x = mod(u, 1) * cells;
  const y = mod(v, 1) * cells;
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const tx = smooth(x - ix);
  const ty = smooth(y - iy);
  const h = (dx: number, dy: number) => hash(mod(ix + dx, cells), mod(iy + dy, cells), seed);
  return (h(0, 0) * (1 - tx) + h(1, 0) * tx) * (1 - ty) + (h(0, 1) * (1 - tx) + h(1, 1) * tx) * ty;
}

/** Cellular distance and borders for pebbled, hammered and cracked organic glass. */
function cells(u: number, v: number, count: number, seed: number) {
  const x = mod(u, 1) * count;
  const y = mod(v, 1) * count;
  let first = 9;
  let second = 9;
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const ix = Math.floor(x) + dx;
      const iy = Math.floor(y) + dy;
      const px = ix + 0.15 + hash(mod(ix, count), mod(iy, count), seed) * 0.7;
      const py = iy + 0.15 + hash(mod(ix, count), mod(iy, count), seed + 29) * 0.7;
      const d = Math.hypot(x - px, y - py);
      if (d < first) {
        second = first;
        first = d;
      } else if (d < second) second = d;
    }
  }
  return { distance: first, border: second - first };
}

/** A sampled surface (roughness and height), not a photograph/diffuse colour map. */
export function sampleProformGlassPattern(pattern: ProformGlassPattern, u: number, v: number) {
  const x = mod(u, 1);
  const y = mod(v, 1);
  const half = Math.min(0.49, pattern.featureSizeMm / pattern.pitchMm / 2);
  const feather = 1.5 / 128;
  const inside = (distance: number, radius = half) =>
    smooth((radius + feather - distance) / (2 * feather));
  const seed = pattern.seed ?? 1;
  let clear = 0;
  let height = 0;
  switch (pattern.type) {
    case 'checkerboard':
      clear = smooth(0.5 + (Math.sin(x * tau) * Math.sin(y * tau)) / (feather * 4));
      height = clear;
      break;
    case 'clear-squares':
      clear = inside(Math.max(Math.abs(x - 0.5), Math.abs(y - 0.5)));
      height = clear;
      break;
    case 'dots':
    case 'lenses': {
      const distance = Math.hypot(x - 0.5, y - 0.5);
      clear = inside(distance);
      height =
        pattern.type === 'lenses' ? Math.sqrt(Math.max(0, 1 - (distance / half) ** 2)) : clear;
      break;
    }
    case 'stripes':
      clear = inside(Math.abs(x - 0.5));
      height = clear;
      break;
    case 'flutes':
      height = (1 - Math.cos(x * tau)) / 2;
      clear = 0.55 + height * 0.45;
      break;
    case 'tiles': {
      const d = Math.max(Math.abs(x - 0.5), Math.abs(y - 0.5));
      clear = inside(d);
      height = clear * (0.2 + 0.8 * (1 - d * 2));
      break;
    }
    case 'crosshatch': {
      const a = Math.abs(mod(x + y, 1) - 0.5);
      const b = Math.abs(mod(x - y, 1) - 0.5);
      height = Math.max(inside(a), inside(b));
      clear = 1 - height;
      break;
    }
    case 'woven': {
      const warp = (1 - Math.cos(x * tau)) / 2;
      const weft = (1 - Math.cos(y * tau)) / 2;
      height = warp * 0.6 + weft * 0.3 + warp * weft * 0.1;
      clear = height;
      break;
    }
    case 'waves':
      height =
        0.5 +
        0.24 * Math.sin(tau * (x * 3 + 0.32 * Math.sin(y * tau))) +
        0.16 * Math.sin(tau * (y * 2 + 0.2 * Math.sin(x * tau))) +
        0.1 * (noise(x, y, 8, seed) - 0.5);
      clear = 0.45 + height * 0.45;
      break;
    case 'bark':
      height =
        0.5 +
        0.32 * Math.sin(tau * (x * 9 + noise(x, y, 4, seed) * 0.9)) +
        0.15 * Math.sin(tau * (x * 17 + Math.sin(y * tau * 3) * 0.3));
      clear = height * 0.8;
      break;
    case 'delta': {
      const cell = cells(x + Math.sin(y * tau) * 0.08, y + Math.sin(x * tau) * 0.08, 6, seed);
      height = 1 - smooth(cell.border * 9);
      clear = 1 - height * 0.8;
      break;
    }
    case 'ice': {
      const cell = cells(x, y, 9, seed);
      height = clamp01(cell.distance * 0.6 + (1 - smooth(cell.border * 15)) * 0.4);
      clear = 0.15 + height * 0.65;
      break;
    }
    case 'hammered': {
      const count = Math.max(2, Math.min(12, Math.round(pattern.pitchMm / pattern.featureSizeMm)));
      height = 1 - smooth(cells(x, y, count, seed).distance);
      clear = 0.2 + height * 0.65;
      break;
    }
    case 'grain':
      height = noise(x, y, 16, seed) * 0.65 + noise(x, y, 32, seed + 1) * 0.35;
      clear = height * 0.75;
      break;
  }
  return {
    height: clamp01(height),
    roughness: clamp01(
      pattern.frostedRoughness +
        (pattern.clearRoughness - pattern.frostedRoughness) * clamp01(clear),
    ),
  };
}
